import { usersKeys, type TUser } from "@/shared/services";
import type { IProfileData, TOrgInfo } from "@/types";
import type { StoryType } from "@config";
import { useAccountStore } from "@features/account-management";
import { reactQueryUtils } from "@lib/react-query";
import { getAccount } from "@store/account/selectors";
import { store } from "@store/store";
import { produce } from "immer";
import moment from "moment";
import { productionTemplate, stagingTemplate } from "../templates";
import { stripEditorOnlySettings } from "./strip-editor-only-settings";
import { buildSenderData } from "@store/fast-versions/data/sender-data";

const SENDER_BLOCK_FIELDS = [
  "name",
  "email",
  "picture",
  "calendar",
  "role",
  "phone",
  "department",
  "website",
  "signature",
] as const;

export const renderStory = ({
  story,
  preview,
}: {
  story: StoryType;
  preview: boolean;
}) => {
  const timestamp = moment().format("mmssDDMMYYYY");
  const env = import.meta.env.MODE;
  // Dev server only: VITE_LOCAL_PRESENTATION_BUNDLE (set in the git-ignored
  // .env.local) points Present and previews at a locally built static-library
  // bundle, to try viewer changes before they deploy. It must be an https URL
  // (e.g. a `cloudflared tunnel --url` to the build): previews are hosted on
  // storydoc.com, and browsers block those pages from loading localhost.
  const bundleBase =
    (import.meta.env.DEV && import.meta.env.VITE_LOCAL_PRESENTATION_BUNDLE) ||
    `https://www.storydoc.com/assets/static-bundle/${env}`;
  const bundleCss = `${bundleBase}/presentation-bundle.css?${timestamp}`;
  const bundleJs = `${bundleBase}/presentation-bundle.js?${timestamp}`;
  const state = store.getState();

  /**
   * The org record the templates render with: the account store first, which
   * the rest of the app reads and which useGetAccountQuery keeps filled, then
   * the RTK Query slot behind getOrgInfo. The renderer used to read only the
   * latter, and that slot was empty during a duplicate from the stories grid —
   * the template dereferenced it, and the copy was created without its preview.
   */
  const account =
    (useAccountStore.getState().account as unknown as TOrgInfo | null) ??
    getAccount(state);
  const user = reactQueryUtils.getQueryData<TUser>([...usersKeys.currentUser]);
  story = produce(story, (draft) => {
    draft.slides = draft.slides?.filter((slide) => !slide.settings?.isHidden);
    // The whole story is serialized into `window.story` below, so editor-only
    // settings would be readable from the console by every reader of the
    // published page. Strip them from this render copy only.
    stripEditorOnlySettings(draft);
  });

  // The reader header's sender block, embedded in the page source: only the
  // fields it has always carried (the header reads name, picture and
  // calendar). The rest of the sender data — address, location, LinkedIn,
  // profile custom fields — reaches a deck only as resolved variable values
  // where the deck uses them, never as page-wide data.
  const senderData = buildSenderData(user);
  const sender = Object.fromEntries(
    SENDER_BLOCK_FIELDS.map((field) => [field, senderData[field]]),
  ) as unknown as IProfileData["sender"];
  const senderInformation = {
    sender,
    org: {
      title: account?.title,
      website: account?.website,
      logo: account?.customization?.logo,
    },
  } as IProfileData;

  const rendered =
    env === "production"
      ? productionTemplate({
          story,
          bundleCss,
          bundleJs,
          orgInfo: account,
          senderInformation,
          isPreview: preview,
        })
      : stagingTemplate({
          story,
          bundleCss,
          bundleJs,
          orgInfo: account,
          isPreview: preview,
          senderInformation,
        });

  return rendered;
};
